<script lang="ts" setup>
import { ArrowRightIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/24/outline'
import { breakpointsTailwind, useBreakpoints, useScrollLock } from '@vueuse/core'
import { useAcademyScrollRestore } from '../composables/useAcademyScrollRestore'

interface Chapter {
  title: string
  count: number
  to: string
  path?: string
}

interface ArticleLink {
  title: string
  path: string
}

interface ArticleGroup {
  top?: string
  sub?: string
  articles: ArticleLink[]
}

interface CurrentChapter {
  title: string
  count: number
  path: string
  articles: ArticleLink[]
  groups: ArticleGroup[]
}

const props = defineProps<{
  chapters: Chapter[]
  currentChapter?: CurrentChapter
  currentPath?: string
  overviewPath: string
}>()

const open = defineModel<boolean>('open', { default: false })
const isChapterExpanded = ref(true)
const mobileNavRef = ref<HTMLElement | null>(null)
const mobileNavScrollKey = computed(() => props.currentChapter?.path ?? 'academy')

const { saveScroll: saveMobileNavScroll } = useAcademyScrollRestore(mobileNavRef, {
  activeSelector: '[aria-current="page"]',
  activeOffset: 112,
  isVisible: open,
  scrollKey: mobileNavScrollKey,
  stateKey: 'academy:mobile-nav-scroll-positions',
})

const breakpoints = useBreakpoints(breakpointsTailwind)
const isDesktop = breakpoints.greaterOrEqual('md')

watch(isDesktop, (value) => {
  if (value) {
    open.value = false
  }
}, {
  immediate: true,
})

const isBodyScrollLocked = useScrollLock(
  import.meta.client ? document.body : null,
)

watch(open, (isOpen) => {
  isBodyScrollLocked.value = isOpen
})

onBeforeUnmount(() => {
  isBodyScrollLocked.value = false
})

function close() {
  open.value = false
}

const filteredChapters = computed(() => {
  if (!props.currentChapter?.path) {
    return props.chapters
  }
  return props.chapters.filter(c => c.path !== props.currentChapter?.path)
})

function isArticleActive(path: string) {
  return path === props.currentPath
}

function articleStartIndex(groupIndex: number): number {
  if (!props.currentChapter) {
    return 0
  }
  let total = 0
  for (let i = 0; i < groupIndex; i++) {
    total += props.currentChapter.groups[i]?.articles.length ?? 0
  }
  return total
}
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <Transition
        enter-active-class="transform transition ease-in-out duration-300"
        leave-active-class="transform transition ease-in-out duration-200"
        enter-from-class="-translate-y-full"
        leave-to-class="-translate-y-full"
      >
        <div
          v-if="open"
          class="fixed inset-x-0 top-[calc(60px+44px)] bottom-0 z-30 md:hidden bg-page font-display flex flex-col will-change-transform"
          role="dialog"
          aria-modal="true"
        >
          <nav ref="mobileNavRef" aria-label="Academy mobile navigation" class="flex-1 overflow-y-auto" @click.capture="saveMobileNavScroll">
            <div class="sticky top-0 z-[5] bg-page border-b border-default px-4 py-2">
              <NuxtLink
                :to="overviewPath"
                class="flex items-center py-4 text-sm font-semibold text-primal"
                @click="close"
              >
                Overview
              </NuxtLink>
            </div>

            <div v-if="currentChapter" class="border-b border-default px-4 py-2">
              <button
                type="button"
                class="flex items-center gap-2 py-4 w-full"
                :aria-expanded="isChapterExpanded"
                @click="isChapterExpanded = !isChapterExpanded"
              >
                <span class="flex-1 text-sm font-semibold text-primal text-left">
                  {{ currentChapter.title }}
                  <span class="text-neutral-900/30">({{ currentChapter.count }})</span>
                </span>
                <component
                  :is="isChapterExpanded ? ChevronUpIcon : ChevronDownIcon"
                  class="size-5 shrink-0"
                  aria-hidden="true"
                />
              </button>
              <div v-if="isChapterExpanded" class="flex flex-col pb-5">
                <template v-for="(group, groupIndex) in currentChapter.groups" :key="groupIndex">
                  <div
                    v-if="group.top"
                    class="pt-5 pb-2 text-xs font-semibold text-primal leading-snug"
                  >
                    <div>{{ group.sub ? `${group.top}:` : group.top }}</div>
                    <div v-if="group.sub">
                      {{ group.sub }}
                    </div>
                  </div>
                  <div class="flex flex-col gap-px">
                    <NuxtLink
                      v-for="(article, articleIndex) in group.articles"
                      :key="article.path"
                      :to="article.path"
                      class="flex items-start gap-2 p-2 rounded-lg"
                      :class="{ 'bg-neutral-600/30': isArticleActive(article.path) }"
                      :aria-current="isArticleActive(article.path) ? 'page' : undefined"
                      @click="close"
                    >
                      <span class="text-xs text-secondary shrink-0">{{ articleStartIndex(groupIndex) + articleIndex + 1 }}.</span>
                      <span class="text-xs text-secondary min-w-0">{{ article.title }}</span>
                    </NuxtLink>
                  </div>
                </template>
              </div>
            </div>

            <div class="border-default px-4 py-2">
              <div class="flex items-center justify-center py-5">
                <span class="flex-1 text-sm text-secondary">
                  {{ currentChapter ? 'Other Chapters' : 'Chapters' }}
                </span>
              </div>
              <NuxtLink
                v-for="chapter in filteredChapters"
                :key="chapter.to"
                :to="chapter.to"
                class="flex items-center gap-2 py-4"
                @click="close"
              >
                <span class="flex-1 text-sm font-semibold text-primal">
                  {{ chapter.title }}
                  <span class="text-neutral-900/30">({{ chapter.count }})</span>
                </span>
                <ArrowRightIcon class="size-5 shrink-0" aria-hidden="true" />
              </NuxtLink>
            </div>
          </nav>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>
